import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import axios from 'utils/axios';
import AxiosAdmin from 'utils/axiosAdmin';
import URL from 'config/url';

const formatQueryParam = (value) => {
  if (value === null || value === undefined) return '';
  if (Array.isArray(value)) return value.length > 0 ? value.map(String).join(',') : '';
  return String(value);
};

export const buildHotelBookingsQuery = (data, options = {}) => {
  const { csv = false } = options;
  const params = new URLSearchParams();

  params.set('page', String(data.page || 1));
  params.set('limit', String(data.limit || 10));
  if (data.pageSize !== undefined && data.pageSize !== null) {
    params.set('page_size', String(data.pageSize));
  }
  if (csv) params.set('csv', 'true');
  if (data.id) params.set('id', data.id);

  params.set('booking_start_date', data.booking_start_date ?? data.startDate ?? '');
  params.set('booking_end_date', data.booking_end_date ?? data.endDate ?? '');
  params.set('travel_start_date', data.travel_start_date ?? data.travelStartDate ?? '');
  params.set('travel_end_date', data.travel_end_date ?? data.travelEndDate ?? '');
  params.set('booking_status', formatQueryParam(data.booking_status ?? data.bookingStatus));
  params.set('provider_name', formatQueryParam(data.provider_name ?? data.provider));
  params.set('currency', formatQueryParam(data.currency));
  params.set('account_name', formatQueryParam(data.account_name ?? data.agencyName));
  params.set('account_id', formatQueryParam(data.account_id ?? data.agencyId));
  params.set('refundable', formatQueryParam(data.refundable));
  params.set('text', data.text ?? '');

  const sortingField = data.sorting_field ?? data.sortingField;
  if (sortingField) params.set('sorting_field', sortingField);

  const sorting = data.sorting;
  if (sorting === 'ascend') params.set('sorting', 'asc');
  else if (sorting === 'descend') params.set('sorting', 'desc');
  else if (sorting === 'asc' || sorting === 'desc') params.set('sorting', sorting);

  params.set('payment_gateway', formatQueryParam(data.payment_gateway ?? data.paymentGateway));
  params.set('payment_method_type', formatQueryParam(data.payment_method_type ?? data.paymentMethodTypes));
  params.set('user_type', formatQueryParam(data.user_type ?? data.userType));
  params.set('cogs', formatQueryParam(data.cogs));
  params.set('non_login', formatQueryParam(data.non_login ?? data.isNonLogin));
  params.set('refund_protect', formatQueryParam(data.refund_protect));
  params.set('cogs_paid_date', data.cogs_paid_date ?? data.cogsPaidDate ?? '');
  params.set('agency_commission_paid', formatQueryParam(data.agency_commission_paid ?? data.agencyCommissionPaid));
  params.set('agency_commission_paid_date', data.agency_commission_paid_date ?? data.agencyCommissionPaidDate ?? '');
  params.set('vendor_cogs_payment_mode', formatQueryParam(data.vendor_cogs_payment_mode ?? data.cogsPaymentMode));
  params.set('aggregator', formatQueryParam(data.aggregator));

  return params.toString();
};

export const getHotels = createAsyncThunk('GET HOTELS', async (data, thunkAPI) => {
  const query = buildHotelBookingsQuery(data);
  const response = await AxiosAdmin.get(`${URL.HOTEL}?${query}`, thunkAPI);
  return response.data;
});

export const getHotelDetails = createAsyncThunk('GET HOTEL DETAILS', async (data, thunkAPI) => {
  const response = await AxiosAdmin.get(`${URL.HOTEL_BOOKING_DETAILS_V2}/${data.referenceNumber}`, thunkAPI);
  return response.data;
});

export const resendEmailConfirmation = createAsyncThunk(
  'RESEND_HOTEL_EMAIL_CONFIRMATION',
  async ({ requestData, v2Version }, thunkAPI) => {
    if (v2Version) {
      const response = await AxiosAdmin.post(URL.RESEND_HOTEL_CONFIRMATION_EMAIL_V2, requestData, thunkAPI);
      return response.data;
    } else {
      const response = await axios.post(URL.RESEND_HOTEL_CONFIRMATION_EMAIL, requestData, thunkAPI);
      return response.data;
    }
  }
);

export const updateHotelRemarks = createAsyncThunk('UPDATE HOTEL_REMARKS', async (data, thunkAPI) => {
  const bookingId = data.booking_id ?? data.id ?? data.referenceNumber;
  const requestData = {
    remark_id: data?.remark_id || '',
    remark: data?.remark,
  };
  const response = await AxiosAdmin.patch(`${URL.HOTEL_UPDATE}/${bookingId}`, requestData, thunkAPI);
  return response.data;
});

export const updateHotelBooking = createAsyncThunk('UPDATE HOTEL BOOKING', async (data, thunkAPI) => {
  const bookingId = data.booking_id ?? data.id ?? data.referenceNumber;
  const { booking_id, id, referenceNumber, ...payload } = data;
  const response = await AxiosAdmin.patch(`${URL.HOTEL_UPDATE}/${bookingId}`, payload, thunkAPI);
  return response.data;
});

const initialState = {
  isLoading: false,
  hotelData: null,
  hotelDetails: null,
};

export const hotelSlice = createSlice({
  name: 'hotel',
  initialState,
  reducers: {},
  extraReducers: {
    // get hotels
    [getHotels.pending]: (state) => {
      state.isLoading = true;
      // Don't clear hotelData during loading to prevent table from going blank
      // state.hotelData will be updated when fulfilled
    },
    [getHotels.fulfilled]: (state, action) => {
      state.isLoading = false;
      state.hotelData = action.payload.data;
    },
    [getHotels.rejected]: (state, action) => {
      state.isLoading = false;
      state.hotelData = null;
      toast.error(action?.payload?.message);
    },

    // update hotel remarks
    [updateHotelRemarks.pending]: (state) => {
      state.isLoading = true;
    },
    [updateHotelRemarks.fulfilled]: (state, action) => {
      state.isLoading = false;
      toast.success('Remarks updated successfully');
    },
    [updateHotelRemarks.rejected]: (state, action) => {
      state.isLoading = false;
      toast.error(action.payload?.message || 'Error updating remarks');
    },

    // get hotel Details
    [getHotelDetails.pending]: (state) => {
      state.isLoading = true;
      state.hotelDetails = null;
    },
    [getHotelDetails.fulfilled]: (state, action) => {
      state.isLoading = false;
      const details = action.payload?.data;
      state.hotelDetails = details?.bookings?.[0] || details;
    },
    [getHotelDetails.rejected]: (state, action) => {
      state.isLoading = false;
      state.hotelDetails = null;
      toast.error(action?.payload?.message);
    },

    // resend hotel confirmation email
    [resendEmailConfirmation.pending]: (state) => {
      state.isLoading = true;
    },
    [resendEmailConfirmation.fulfilled]: (state) => {
      state.isLoading = false;
      // Handle success, e.g., show a success toast message
      toast.success('Email confirmation resent successfully');
    },
    [resendEmailConfirmation.rejected]: (state, action) => {
      state.isLoading = false;
      // Handle error, e.g., show an error toast message
      toast.error(action.payload?.error || 'Error resending email confirmation');
    },

    // update hotel booking
    [updateHotelBooking.pending]: (state) => {
      state.isLoading = true;
    },
    [updateHotelBooking.fulfilled]: (state, action) => {
      state.isLoading = false;
      toast.success(action.payload?.message);
    },
    [updateHotelBooking.rejected]: (state, action) => {
      state.isLoading = false;
      toast.error(action.payload?.message || 'Error updating cogs data');
    },
  },
});

export default hotelSlice.reducer;
